// TUT-510 — o LADRILHO do assunto, primitivo próprio do Lumen.
//
// Nasceu no cartão da Visão geral (TUT-464): o ícone do título vestido, por
// seletor descendente, como um chip sólido na cor do assunto. Só os widgets o
// tinham, e o dono escolheu justamente esse acento ("a cor como acento") como
// a identidade das telas em 14/10. Virou peça para as outras telas o usarem
// sem copiar o seletor: o título do painel (`PanelTitle`, quando o `Panel`
// declara o assunto), o canto do cartão-resumo (`FaixaDeIndicadores`) e o
// cartão da Visão geral, que deixou de vestir o próprio `<svg>`.
//
// O glifo é sempre `--assunto-glifo` (navy), medido a ≥ 5,8:1 sobre cada
// sólido nos dois temas (`contrast.test.ts`). As classes de cor são literais
// porque o Tailwind só gera o que lê escrito.
//
// TUT-539 (D2/D5/D6) — o ladrilho também é o SELO do tipo: com `tipo`, o
// sólido é a cor do tipo (`cor-do-tipo.ts`), o único sólido do cartão
// tingido. Com `assunto`, segue o ícone do painel. A paleta por assunto foi
// revista na mesma decisão: prazos neutro, laudos verde-folha (ver o tema).
//
// TUT-582 (dono, 02/11, Q5 e a prévia B) — a cor de ASSUNTO foi aposentada:
// o quadrado na cor do assunto repetia o que o título já dizia, e no quadro
// mentia (o cartão sem tipo da Elaboração lia-se "laudo"). Com `assunto`, o
// ladrilho é NEUTRO (o realce neutro e o glifo esmaecido, par medido no
// `contrast.test.ts`) e mora só no título do painel (`PanelTitle`) e no
// cabeçalho da ficha (`FichaDaCasa`). Saiu dos fatos da ficha, do canto do
// cartão-resumo, dos widgets, do cabeçalho de aba e dos cartões de quadro. A
// cor só fica onde ela diz algo: o tipo (o selo, abaixo) e a situação.

import type { ComponentType, ReactNode } from "react";
import { Briefcase, CalendarClock, FileText, FolderOpen, ListChecks, Scale, ScanSearch, Users } from "lucide-react";

import { ROTULO_DO_TIPO, estiloDoSelo, type TipoDeItem } from "../../lib/cor-do-tipo";
import { cn } from "../../lib/utils";
import type { AssuntoDaFaixa } from "./anel-de-recorte";

export type IconeDoLadrilho = ComponentType<{
  "aria-hidden"?: boolean | "true";
  className?: string;
}>;

/**
 * O ícone de cada assunto quando a tela não escolhe um. Existe para o painel
 * que só declara o assunto não precisar inventar o glifo.
 */
export const ICONE_PADRAO_DO_ASSUNTO: Record<AssuntoDaFaixa, IconeDoLadrilho> = {
  prazos: CalendarClock,
  pericias: ScanSearch,
  ocorrencias: FolderOpen,
  laudos: FileText,
};

/** TUT-539 — o glifo do selo de cada tipo, quando a tela não escolhe um. */
export const ICONE_PADRAO_DO_TIPO: Record<TipoDeItem, IconeDoLadrilho> = {
  quesito: ListChecks,
  pericia: ScanSearch,
  laudo: FileText,
  impugnacao: Scale,
  reuniao: Users,
  consultoria: Briefcase,
};

const TAMANHO = {
  // O do título do painel: 36 px, canto de 12, glifo de 18 (a prévia).
  titulo: "size-9 rounded-[12px] [&>svg:not([class*='size-'])]:size-[1.125rem]",
  // O do canto do cartão-resumo: 30 px, canto de 10, glifo de 15.
  canto: "size-[1.875rem] rounded-[10px] [&>svg:not([class*='size-'])]:size-[0.95rem]",
} as const;

/** O ladrilho: o assunto do painel (neutro) OU o tipo do item (o selo). */
export type CorDoLadrilho =
  | { assunto: AssuntoDaFaixa; tipo?: undefined }
  | { tipo: TipoDeItem; assunto?: undefined };

export function Ladrilho({
  assunto,
  tipo,
  icone,
  tamanho = "titulo",
  className,
  children,
}: CorDoLadrilho & {
  icone?: IconeDoLadrilho | undefined;
  tamanho?: keyof typeof TAMANHO;
  className?: string;
  /** O glifo já montado (o `icone` de um indicador); vence o `icone`. */
  children?: ReactNode;
}) {
  const Icone = icone ?? (tipo ? ICONE_PADRAO_DO_TIPO[tipo] : ICONE_PADRAO_DO_ASSUNTO[assunto]);
  return (
    <span
      aria-hidden="true"
      data-slot="ladrilho"
      data-assunto={tipo ? undefined : assunto}
      data-tipo={tipo}
      // TUT-539 — o sólido do tipo vai em linha (não custa CSS). TUT-582 — o
      // do assunto é neutro.
      style={estiloDoSelo(tipo)}
      className={cn(
        "grid flex-none place-items-center",
        TAMANHO[tamanho],
        tipo ? "text-[var(--assunto-glifo)]" : "bg-[var(--neutral-highlight)] text-[var(--muted)]",
        className,
      )}
    >
      {children ?? <Icone aria-hidden="true" />}
    </span>
  );
}

/**
 * TUT-539 (decisão do dono, 24/10) — o SELO DO TIPO: o chip sólido com a
 * PALAVRA do tipo ("Perícia", "Laudo"), como na prévia A. É o único sólido do
 * cartão tingido e da linha da lista: ele substitui o ladrilho com glifo como
 * selo, não se soma a ele. O sólido vai em linha (`estiloDoSelo`, sem CSS por
 * tipo) e a palavra é o navy do glifo (`--assunto-glifo`), medida a 4,5:1 ou
 * mais sobre cada sólido nos dois temas (`contrast.test.ts`). As classes são
 * utilitárias que o CSS de entrada já tinha: o chip não custa regra.
 */
export const seloDoTipoClass =
  "inline-flex flex-none items-center whitespace-nowrap rounded-full px-[0.45rem] py-[0.15rem] text-legenda font-semibold leading-none text-[var(--assunto-glifo)]";

export function SeloDoTipo({ tipo, className }: { tipo: TipoDeItem; className?: string }) {
  return (
    <span data-slot="selo-do-tipo" data-tipo={tipo} style={estiloDoSelo(tipo)} className={cn(seloDoTipoClass, className)}>
      {ROTULO_DO_TIPO[tipo]}
    </span>
  );
}
